import type { ChatMessage } from "@tracify/shared";
import { formatToolName } from "@/components/ai-elements/tool-call";

export type ToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;

const mediaBuyingArtifactOutputTypes = [
  "campaign_draft",
  "adset_draft",
  "ad_draft",
  "creative_draft",
  "markdown",
] as const;

const mediaBuyingActionTypes = [
  "create",
  "update",
  "set_status",
  "set_budget",
  "duplicate",
  "delete",
] as const;

export type SavedArtifactOutputType =
  (typeof mediaBuyingArtifactOutputTypes)[number];
export type MediaBuyingActionType = (typeof mediaBuyingActionTypes)[number];
export type MediaBuyingEntityType = "campaign" | "adset" | "ad" | "creative";

export type MediaBuyingActionTarget = {
  platform: string;
  entityType: MediaBuyingEntityType;
  entityId: string | null;
  entityName: string | null;
  campaignId: string | null;
  adsetId: string | null;
};

export type OutputCardModel =
  | {
      kind: "image-generation";
      title: string;
      prompt: string | null;
      text: string | null;
      images: Array<{
        mediaType: string;
        url: string;
      }>;
    }
  | {
      kind: "media-buying-output";
      title: string;
      objectType: SavedArtifactOutputType | string;
      outputId: string | null;
      revision: number | null;
      bodyMarkdown: string | null;
      data: Record<string, unknown> | null;
      renderHint: string | null;
    }
  | {
      kind: "markdown";
      title: string;
      outputId: string | null;
      revision: number | null;
      bodyMarkdown: string;
      data: Record<string, unknown> | null;
    }
  | {
      kind: "generic";
      title: string;
      outputId: string | null;
      revision: number | null;
      input: unknown;
      output: unknown;
    };

export type ActionCardModel =
  | {
      kind: "media-buying-action";
      title: string;
      actionId: string | null;
      status: string | null;
      capabilityKey: string | null;
      actionType: MediaBuyingActionType;
      target: MediaBuyingActionTarget;
      changes: Record<string, unknown> | null;
      summary: string | null;
      sourceOutputId: string | null;
      confidence: number | null;
      requiresApproval: boolean;
      actionFingerprint: string | null;
      deduped: boolean;
    }
  | {
      kind: "generic";
      title: string;
      actionId: string | null;
      status: string | null;
      input: unknown;
      output: unknown;
    };

type RecordValue = Record<string, unknown>;

function asRecord(value: unknown): RecordValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as RecordValue;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function readArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function toDataUrl(data: string, mediaType: string) {
  return `data:${mediaType};base64,${data}`;
}

function capitalizeLabel(label: string) {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatEntityTypeLabel(value: string) {
  if (value === "adset") {
    return "Adset";
  }

  return capitalizeLabel(formatToolName(value));
}

export function formatOutputTypeLabel(value: string) {
  return capitalizeLabel(formatToolName(value));
}

export function formatCapabilityLabel(value: string) {
  return capitalizeLabel(formatToolName(value.replace(/\./g, "_")));
}

function isSavedArtifactOutputType(
  value: string
): value is SavedArtifactOutputType {
  return mediaBuyingArtifactOutputTypes.includes(
    value as SavedArtifactOutputType
  );
}

function isMediaBuyingActionType(
  value: string
): value is MediaBuyingActionType {
  return mediaBuyingActionTypes.includes(value as MediaBuyingActionType);
}

function isMediaBuyingEntityType(
  value: string
): value is MediaBuyingEntityType {
  return (
    value === "campaign" ||
    value === "adset" ||
    value === "ad" ||
    value === "creative"
  );
}

function readActionTarget(value: unknown): MediaBuyingActionTarget | null {
  const record = asRecord(value);
  const platform = readString(record?.platform);
  const entityType = readString(record?.entity_type);

  if (!platform || !entityType || !isMediaBuyingEntityType(entityType)) {
    return null;
  }

  return {
    entityType,
    platform,
    entityId: readString(record?.entity_id),
    entityName: readString(record?.entity_name),
    campaignId: readString(record?.campaign_id),
    adsetId: readString(record?.adset_id),
  };
}

function getPartInputRecord(part: ToolPart) {
  return asRecord(part.input);
}

function getPartOutputRecord(part: ToolPart) {
  return asRecord(part.output);
}

function readGenerateImageTitle(input: unknown) {
  const record = asRecord(input);
  return readString(record?.title);
}

function readGenerateImagePrompt(input: unknown) {
  const record = asRecord(input);
  const message = asRecord(record?.message);
  const parts = readArray(message?.parts);
  if (!parts) {
    return null;
  }

  const prompt = parts
    .map((part) => asRecord(part))
    .filter((part): part is RecordValue => Boolean(part))
    .filter((part) => part.type === "text")
    .map((part) => readString(part.text))
    .filter((part): part is string => Boolean(part))
    .join(" ");

  return prompt.length > 0 ? prompt : null;
}

function dedupeGeneratedImages(
  images: Array<{
    mediaType: string;
    url: string;
  }>
) {
  const seen = new Set<string>();

  return images.filter((image) => {
    const key = `${image.mediaType}:${image.url}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function readToolResultContentParts(output: unknown) {
  const record = asRecord(output);
  if (record?.type !== "content") {
    return [];
  }

  return (
    readArray(record.value)
      ?.map((part) => asRecord(part))
      .filter((part): part is RecordValue => Boolean(part)) ?? []
  );
}

function readGeneratedImageText(output: unknown) {
  const text = readToolResultContentParts(output)
    .filter((part) => part.type === "text")
    .map((part) => readString(part.text))
    .filter((part): part is string => Boolean(part))
    .join(" ");

  return text.length > 0 ? text : null;
}

function readGeneratedImages(output: unknown) {
  return dedupeGeneratedImages(
    readToolResultContentParts(output)
      .flatMap((part) => {
        if (part.type === "file") {
          const mediaType = readString(part.mediaType);
          const data = asRecord(part.data);
          if (!mediaType || !mediaType.startsWith("image/")) {
            return [];
          }

          if (data?.type === "data") {
            const base64 = readString(data.data);
            if (!base64) {
              return [];
            }

            return [
              {
                mediaType,
                url: toDataUrl(base64, mediaType),
              },
            ];
          }

          if (data?.type === "url") {
            const url = readString(data.url);
            if (!url) {
              return [];
            }

            return [
              {
                mediaType,
                url,
              },
            ];
          }

          return [];
        }

        if (part.type === "image-data") {
          const data = readString(part.data);
          const mediaType = readString(part.mediaType);
          if (!data || !mediaType) {
            return [];
          }

          return [
            {
              mediaType,
              url: toDataUrl(data, mediaType),
            },
          ];
        }

        if (part.type === "image-url") {
          const url = readString(part.url);
          if (!url) {
            return [];
          }

          return [
            {
              mediaType: readString(part.mediaType) ?? "image/*",
              url,
            },
          ];
        }

        return [];
      })
      .filter(
        (
          image
        ): image is {
          mediaType: string;
          url: string;
        } => Boolean(image.mediaType && image.url)
      )
  );
}

export function getOutputTitle(part: ToolPart) {
  if (part.type === "tool-generateImage") {
    return (
      readGenerateImageTitle(part.input) ??
      readGenerateImagePrompt(part.input) ??
      "Generated image"
    );
  }

  return readString(getPartInputRecord(part)?.title) ?? "Saved output";
}

export function getActionTitle(part: ToolPart) {
  const input = getPartInputRecord(part);
  const explicitTitle = readString(input?.title);

  if (explicitTitle) {
    return explicitTitle;
  }

  const actionType = readString(input?.action_type);
  const target = readActionTarget(input?.target);
  if (actionType && target && isMediaBuyingActionType(actionType)) {
    return `${formatCapabilityLabel(actionType)} ${
      target.entityName ?? formatEntityTypeLabel(target.entityType)
    }`;
  }

  const capabilityKey = readString(input?.capability_key);
  return capabilityKey
    ? formatCapabilityLabel(capabilityKey)
    : "Proposed action";
}

export function getOutputCardModel(part: ToolPart): OutputCardModel {
  const input = getPartInputRecord(part);
  const output = getPartOutputRecord(part);
  const title = getOutputTitle(part);
  const prompt =
    part.type === "tool-generateImage"
      ? readGenerateImagePrompt(part.input)
      : null;

  if (part.type === "tool-generateImage") {
    const images = readGeneratedImages(part.output);
    if (images.length > 0) {
      return {
        kind: "image-generation",
        title,
        prompt,
        text: readGeneratedImageText(part.output),
        images,
      };
    }
  }

  const outputId = readString(output?.id);
  const revision = readNumber(output?.revision);
  const bodyMarkdown = readString(input?.body_markdown);
  const data = asRecord(input?.data);
  const renderHint = readString(input?.render_hint);
  const objectType = readString(input?.object_type);

  if (objectType && isSavedArtifactOutputType(objectType)) {
    return {
      kind: "media-buying-output",
      title,
      objectType,
      outputId,
      revision,
      bodyMarkdown,
      data,
      renderHint,
    };
  }

  if (bodyMarkdown) {
    return {
      kind: "markdown",
      title,
      outputId,
      revision,
      bodyMarkdown,
      data,
    };
  }

  return {
    kind: "generic",
    title,
    outputId,
    revision,
    input: part.input,
    output: part.output,
  };
}

export function getActionCardModel(part: ToolPart): ActionCardModel {
  const input = getPartInputRecord(part);
  const output = getPartOutputRecord(part);
  const title = getActionTitle(part);
  const actionId = readString(output?.id);
  const status = readString(output?.status);
  const actionType = readString(input?.action_type);
  const target = readActionTarget(input?.target);

  if (actionType && target && isMediaBuyingActionType(actionType)) {
    return {
      kind: "media-buying-action",
      title,
      actionId,
      status,
      capabilityKey: readString(input?.capability_key),
      actionType,
      target,
      changes: asRecord(input?.changes),
      summary: readString(input?.summary),
      sourceOutputId: readString(input?.source_output_id),
      confidence: readNumber(input?.confidence),
      requiresApproval: readBoolean(input?.requires_approval) ?? true,
      actionFingerprint: readString(output?.action_fingerprint),
      deduped: readBoolean(output?.deduped) ?? false,
    };
  }

  return {
    kind: "generic",
    title,
    actionId,
    status,
    input: part.input,
    output: part.output,
  };
}
